import React, { useEffect, useState } from "react";
import _ from "lodash";
import Joi from "joi-browser";
import uuid from "react-uuid";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, Button, Card, Col, Collapse, Form, Modal, Row, Spinner } from "react-bootstrap";
import { getMarketsWithAttributes } from "../../common/services/markets/marketService";
import { getTermsByDate } from "../../common/services/markets/termService";
import { getProducts } from "../../common/services/products/productService";
import { getFilteredProductForMarket } from "../../common/services/products/productUtilities";
import { getOrAddInstrument } from "../../common/services/marketdata/instrumentService";
import { getTradeNegotiationMetadataFieldsByMarket } from "../../common/services/marketdata/tradeNegotiationMetadataService";
import {
  acceptTradeConfirmation,
  cancelTradeConfirmation,
  getTradeConfirmationByEngineTrade,
  getTradeConfirmationVersions,
  rejectTradeConfirmation,
  saveTradeConfirmation,
  uploadTradeConfirmationAttachment,
} from "../../common/services/marketdata/tradeConfirmationService";
import { formatDate, formatDateTime } from "../../common/services/utilities";
import "./tradeConfirmationModal.css";

const emptyGuid = "00000000-0000-0000-0000-000000000000";

function toDateTimeInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 16);
}

function toDateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function parseMultiSelectValue(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value.split(",").filter(Boolean);
  }
}

function formatVersionDate(value) {
  if (!value) return "";
  return formatDateTime(value, Intl.DateTimeFormat().resolvedOptions().timeZone, " ");
}

function normalizeComparableValue(value, fieldName) {
  if (fieldName === "tradeDate" && value) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.getTime();
  }

  if ((fieldName === "price" || fieldName === "volume") && value !== null && value !== undefined && value !== "") {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  }

  return value ?? "";
}

function getBaselineVersion(versionList) {
  if (!versionList || versionList.length < 2) return null;

  const firstVersion = versionList[0];
  const hasNoLastUpdater =
    !firstVersion?.lastUpdatedByOrganizationId || firstVersion.lastUpdatedByOrganizationId === emptyGuid;
  return hasNoLastUpdater ? firstVersion : null;
}

function getDisplayVersions(versionList) {
  const versions = versionList || [];
  return getBaselineVersion(versions) ? versions.slice(1) : versions;
}

function getFieldOptions(field) {
  return field?.options || field?.Options || field?.fieldValueOptions || field?.valueOptions || [];
}

function getComparableConfirmationSnapshot(source) {
  if (!source) return "";

  const instrumentQualityAttributeValues = (source.instrumentQualityAttributeValues || [])
    .filter((value) => value.active !== false)
    .map((value) => ({
      qualityAttributeId: value.qualityAttributeId,
      qualityAttributeValueId: value.qualityAttributeValueId,
    }));

  const qualityAttributeValues = (source.qualityAttributeValues || [])
    .filter((value) => value.active !== false)
    .map((value) => ({
      qualityAttributeId: value.qualityAttributeId,
      qualityAttributeValueId: value.qualityAttributeValueId,
    }));

  const metadataValues = (source.metadataValues || [])
    .filter((value) => value.active !== false)
    .map((value) => ({
      fieldDefinitionId: value.fieldDefinitionId,
      value: value.value ?? "",
      fileName: value.fileName ?? "",
    }));

  return JSON.stringify({
    tradeDate: normalizeComparableValue(source.tradeDate, "tradeDate"),
    price: normalizeComparableValue(source.price, "price"),
    volume: normalizeComparableValue(source.volume, "volume"),
    marketId: source.marketId ?? "",
    termId: source.termId ?? "",
    productId: source.productId ?? "",
    priceBasisId: source.priceBasisId ?? "",
    currencyId: source.currencyId ?? "",
    uomId: source.uomId ?? "",
    instrumentQualityAttributeValues: _.orderBy(instrumentQualityAttributeValues, ["qualityAttributeId"]),
    qualityAttributeValues: _.orderBy(qualityAttributeValues, ["qualityAttributeId"]),
    metadataValues: _.orderBy(metadataValues, ["fieldDefinitionId"]),
  });
}

function TradeConfirmationModal({
  show,
  onHide,
  engineTradeId,
  actingOrganizationId,
  isAdministrator,
  onSaved,
  t,
}) {
  const [confirmation, setConfirmation] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(null);
  const [reviewingVersions, setReviewingVersions] = useState(false);
  const [metadataDefinitions, setMetadataDefinitions] = useState([]);
  const [product, setProduct] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [products, setProducts] = useState([]);
  const [terms, setTerms] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!show || !engineTradeId || !actingOrganizationId) return;

    let isMounted = true;

    async function loadConfirmation() {
      setIsLoading(true);
      setErrors({});
      setReviewingVersions(false);
      setSelectedVersionIndex(null);

      try {
        const loadedConfirmation = await getTradeConfirmationByEngineTrade(
          engineTradeId,
          actingOrganizationId
        );

        if (!isMounted) return;

                const loadedMarkets = await getMarketsWithAttributes();
        const selectedMarket = loadedMarkets.find(
          (market) => market.marketId === loadedConfirmation?.marketId
        );
        const marketDate = formatDate(loadedConfirmation.tradeDate) || new Date().toLocaleDateString("fr-CA");

        const [fields, productWithAttributes, loadedVersions, loadedProducts, loadedTerms] = await Promise.all([
          loadedConfirmation?.marketId
            ? getTradeNegotiationMetadataFieldsByMarket(loadedConfirmation.marketId)
            : Promise.resolve([]),
          loadedConfirmation?.productId && loadedConfirmation?.marketId
            ? getFilteredProductForMarket(
                loadedConfirmation.productId,
                loadedConfirmation.marketId,
                marketDate
              ).then(({ filteredProduct }) => filteredProduct)
            : Promise.resolve(null),
          loadedConfirmation?.exists
            ? getTradeConfirmationVersions(
                loadedConfirmation.tradeConfirmationId,
                actingOrganizationId
              )
            : Promise.resolve([]),
          getProducts(),
          selectedMarket?.termScheduleId
            ? getTermsByDate(selectedMarket.termScheduleId, marketDate)
            : Promise.resolve([]),
        ]);

        if (!isMounted) return;

        const grouped = _.groupBy(fields || [], (field) => field.fieldGroupingName || t("Other"));
        const visibleVersions = getDisplayVersions(loadedVersions || []);
        setConfirmation(loadedConfirmation);
        setMetadataDefinitions(_.orderBy(fields || [], ["fieldGroupingName", "sortOrder", "fieldName"]));
        setProduct(productWithAttributes);
        setMarkets((loadedMarkets || []).filter((market) => market.active !== false));
        setProducts((loadedProducts || []).filter((candidate) => candidate.active !== false));
        setTerms(_.orderBy(loadedTerms || [], ["endDate", "name"]));
        setVersions(loadedVersions || []);
        setSelectedVersionIndex(visibleVersions.length ? visibleVersions.length - 1 : null);
        setOpenGroups(
          Object.keys(grouped).reduce((acc, groupName) => {
            acc[groupName] = true;
            return acc;
          }, {})
        );
      } catch (ex) {
        console.error("Failed to load trade confirmation", ex);
        toast.error(t("TradeConfirmationLoadFailed"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadConfirmation();

    return () => {
      isMounted = false;
    };
  }, [show, engineTradeId, actingOrganizationId, t]);

  function getMarketDate(value = confirmation?.tradeDate) {
    return formatDate(value) || new Date().toLocaleDateString("fr-CA");
  }

  function getFilteredProducts() {
    const marketProductIds = new Set(markets.map((market) => market.productId).filter(Boolean));
    if (!marketProductIds.size) return products;
    return products.filter((candidate) => marketProductIds.has(candidate.productId));
  }

  function getFilteredMarkets(source = confirmation) {
    if (!source?.productId) return markets;
    return markets.filter((market) => market.productId === source.productId);
  }

  function getSelectedMarket(source = confirmation) {
    return markets.find((market) => market.marketId === source?.marketId);
  }

  function getPriceBasisOptions(source = confirmation) {
    const selectedMarket = getSelectedMarket(source);
    const priceBases = (selectedMarket?.marketAttributes || []).filter(
      (marketAttribute) => marketAttribute.active !== false
    );

    if (priceBases.length || !source?.priceBasisId) return priceBases;

    return [
      {
        priceBasisId: source.priceBasisId,
        priceBasisName: source.priceBasisName,
        currencyId: source.currencyId,
        currencyName: source.currencyName,
        currencySymbol: source.currencySymbol,
        uomId: source.uomId,
        uomName: source.uomName,
      },
    ];
  }

  function getSelectedPriceBasis(source = confirmation) {
    return getPriceBasisOptions(source).find(
      (priceBasis) => priceBasis.priceBasisId === source?.priceBasisId
    );
  }

  function applyPriceBasis(nextConfirmation, priceBasis) {
    return {
      ...nextConfirmation,
      priceBasisId: priceBasis?.priceBasisId || "",
      priceBasisName: priceBasis?.priceBasisName || "",
      currencyId: priceBasis?.currencyId || "",
      currencyName: priceBasis?.currencyName || "",
      currencySymbol: priceBasis?.currencySymbol || "",
      uomId: priceBasis?.uomId || "",
      uomName: priceBasis?.uomName || "",
    };
  }

  async function refreshTermsForMarket(market, tradeDate, selectedTermId) {
    if (!market?.termScheduleId) {
      setTerms([]);
      return null;
    }

    const loadedTerms = _.orderBy(
      await getTermsByDate(market.termScheduleId, getMarketDate(tradeDate)),
      ["endDate", "name"]
    );
    setTerms(loadedTerms);
    return loadedTerms.find((term) => term.termId === selectedTermId) || null;
  }

  async function refreshMetadataDefinitions(marketId) {
    if (!marketId) {
      setMetadataDefinitions([]);
      setOpenGroups({});
      return;
    }

    const fields = await getTradeNegotiationMetadataFieldsByMarket(marketId);
    const orderedFields = _.orderBy(fields || [], ["fieldGroupingName", "sortOrder", "fieldName"]);
    const grouped = _.groupBy(orderedFields, (field) => field.fieldGroupingName || t("Other"));
    setMetadataDefinitions(orderedFields);
    setOpenGroups(
      Object.keys(grouped).reduce((acc, groupName) => {
        acc[groupName] = true;
        return acc;
      }, {})
    );
  }

  async function refreshProduct(productId, marketId, tradeDate, pruneExistingValues = true) {
    if (!productId || !marketId) {
      setProduct(null);
      return;
    }

    try {
      const { filteredProduct } = await getFilteredProductForMarket(
        productId,
        marketId,
        getMarketDate(tradeDate)
      );
      const activeValueIds = new Set(
        (filteredProduct?.qualityAttributeValues || []).map((item) => item.qualityAttributeValueId?.toString())
      );

      setProduct(filteredProduct);

      if (!pruneExistingValues) return;

      setConfirmation((currentConfirmation) => {
        if (!currentConfirmation) return currentConfirmation;

        return {
          ...currentConfirmation,
          instrumentQualityAttributeValues: (currentConfirmation.instrumentQualityAttributeValues || []).filter((item) =>
            activeValueIds.has(item.qualityAttributeValueId?.toString())
          ),
          qualityAttributeValues: (currentConfirmation.qualityAttributeValues || []).filter((item) =>
            activeValueIds.has(item.qualityAttributeValueId?.toString())
          ),
        };
      });
    } catch (ex) {
      console.error("Failed to refilter trade confirmation quality attributes", ex);
    }
  }
  if (!show) return null;

  const baselineVersion = getBaselineVersion(versions);
  const displayVersions = getDisplayVersions(versions);
  const currentVersionIndex = displayVersions.length
    ? Math.min(selectedVersionIndex ?? displayVersions.length - 1, displayVersions.length - 1)
    : null;
  const activeConfirmation = reviewingVersions && currentVersionIndex !== null
    ? displayVersions[currentVersionIndex] || confirmation
    : confirmation;
  const latestDisplayVersion = displayVersions.length ? displayVersions[displayVersions.length - 1] : null;
  const isEditingDraft =
    !reviewingVersions &&
    latestDisplayVersion &&
    getComparableConfirmationSnapshot(confirmation) !== getComparableConfirmationSnapshot(latestDisplayVersion);
  const previousVersion = reviewingVersions
    ? currentVersionIndex > 0
      ? displayVersions[currentVersionIndex - 1]
      : baselineVersion
    : isEditingDraft
      ? latestDisplayVersion
      : displayVersions.length > 1
        ? displayVersions[displayVersions.length - 2]
        : baselineVersion;
  const canEdit = !!confirmation?.canEdit && !reviewingVersions && !isSaving;
  const canAccept = !!confirmation?.canAccept && !reviewingVersions && !isSaving;
  const canReject = !!confirmation?.canReject && !reviewingVersions && !isSaving;
  const canCancel = !!confirmation?.canCancel && isAdministrator && !reviewingVersions && !isSaving;
  const qualityAttributes = _.orderBy(
    product?.qualityAttributes || [],
    [(attribute) => (attribute.isPriceDriver ? 0 : 1), "qualityAttributeName"],
    ["asc", "asc"]
  );
  const metadataGroups = _.groupBy(metadataDefinitions, (field) => field.fieldGroupingName || t("Other"));
  const productOptions = getFilteredProducts();
  const marketOptions = getFilteredMarkets(activeConfirmation);
  const priceBasisOptions = getPriceBasisOptions(activeConfirmation);

  function getQualityAttributeValues(source, attribute) {
    return attribute?.isPriceDriver
      ? source?.instrumentQualityAttributeValues || []
      : source?.qualityAttributeValues || [];
  }

  function getQualityAttributeValue(source, attribute) {
    const values = getQualityAttributeValues(source, attribute);
    const matchesAttribute = (value) => value.qualityAttributeId === attribute.qualityAttributeId;

    if (attribute?.isPriceDriver) {
      return values.find(matchesAttribute);
    }

    return values.find((value) => matchesAttribute(value) && value.active !== false) || values.find(matchesAttribute);
  }

  function getMetadataValue(source, fieldDefinitionId) {
    return (source?.metadataValues || []).find(
      (value) => value.fieldDefinitionId === fieldDefinitionId && value.active !== false
    );
  }

  function getChangedClass(changed) {
    return changed ? "trade-confirmation-changed" : undefined;
  }

  function getStatusVariant(status) {
    if (status === "Accepted") return "success";
    if (status === "Rejected" || status === "Cancelled") return "danger";
    if (status === "Pending") return "primary";
    return "secondary";
  }

  function isCoreChanged(fieldName) {
    if (!previousVersion) return false;
    return normalizeComparableValue(previousVersion[fieldName], fieldName) !== normalizeComparableValue(activeConfirmation?.[fieldName], fieldName);
  }

  function isQualityChanged(attribute) {
    const value = getQualityAttributeValue(activeConfirmation, attribute);
    if (value?.changedFromPrevious) return true;
    if (!previousVersion) return false;
    return (
      getQualityAttributeValue(previousVersion, attribute)?.qualityAttributeValueId !==
      value?.qualityAttributeValueId
    );
  }

  function isMetadataChanged(fieldDefinitionId) {
    const value = getMetadataValue(activeConfirmation, fieldDefinitionId);
    if (value?.changedFromPrevious) return true;
    if (!previousVersion) return false;
    return getMetadataValue(previousVersion, fieldDefinitionId)?.value !== value?.value;
  }

  function isMetadataGroupChanged(groupName) {
    return (metadataGroups[groupName] || []).some((field) => isMetadataChanged(field.fieldDefinitionId));
  }

    async function updateConfirmationField(fieldName, value) {
    if (!confirmation) return;

    let nextConfirmation = { ...confirmation, [fieldName]: value };

    if (fieldName === "productId") {
      const selectedProduct = products.find((candidate) => candidate.productId === value);
      const selectedMarket = getSelectedMarket(nextConfirmation);
      nextConfirmation.productName = selectedProduct?.name || "";
      nextConfirmation.instrumentQualityAttributeValues = [];
      nextConfirmation.qualityAttributeValues = [];

      if (selectedMarket?.productId && selectedMarket.productId !== value) {
        nextConfirmation = applyPriceBasis(
          {
            ...nextConfirmation,
            marketId: "",
            marketName: "",
            termId: "",
            termName: "",
            metadataValues: [],
          },
          null
        );
        setTerms([]);
        setMetadataDefinitions([]);
        setOpenGroups({});
        setProduct(null);
      } else {
        await refreshProduct(value, nextConfirmation.marketId, nextConfirmation.tradeDate, false);
      }

      setConfirmation(nextConfirmation);
      return;
    }

    if (fieldName === "marketId") {
      const selectedMarket = markets.find((market) => market.marketId === value);
      const selectedProduct = products.find((candidate) => candidate.productId === selectedMarket?.productId);
      const marketPriceBases = (selectedMarket?.marketAttributes || []).filter(
        (marketAttribute) => marketAttribute.active !== false
      );
      const selectedPriceBasis =
        marketPriceBases.find((priceBasis) => priceBasis.priceBasisId === nextConfirmation.priceBasisId) ||
        marketPriceBases[0];

      nextConfirmation = applyPriceBasis(
        {
          ...nextConfirmation,
          marketName: selectedMarket?.name || "",
          productId: selectedMarket?.productId || nextConfirmation.productId,
          productName: selectedProduct?.name || nextConfirmation.productName || "",
          termId: "",
          termName: "",
          instrumentQualityAttributeValues: [],
          qualityAttributeValues: [],
          metadataValues: [],
        },
        selectedPriceBasis
      );

      setConfirmation(nextConfirmation);

      const selectedTerm = await refreshTermsForMarket(
        selectedMarket,
        nextConfirmation.tradeDate,
        nextConfirmation.termId
      );
      await Promise.all([
        refreshMetadataDefinitions(value),
        refreshProduct(nextConfirmation.productId, value, nextConfirmation.tradeDate, false),
      ]);

      if (selectedTerm) {
        setConfirmation((currentConfirmation) => ({
          ...currentConfirmation,
          termId: selectedTerm.termId,
          termName: selectedTerm.name,
        }));
      }
      return;
    }

    if (fieldName === "termId") {
      const selectedTerm = terms.find((term) => term.termId === value);
      setConfirmation({
        ...nextConfirmation,
        termName: selectedTerm?.name || "",
      });
      return;
    }

    if (fieldName === "priceBasisId") {
      const selectedPriceBasis = getPriceBasisOptions(nextConfirmation).find(
        (priceBasis) => priceBasis.priceBasisId === value
      );
      setConfirmation(applyPriceBasis(nextConfirmation, selectedPriceBasis));
      return;
    }

    setConfirmation(nextConfirmation);

    if (fieldName === "tradeDate" && confirmation?.productId && confirmation?.marketId) {
      await Promise.all([
        refreshTermsForMarket(getSelectedMarket(confirmation), value, confirmation.termId),
        refreshProduct(confirmation.productId, confirmation.marketId, value),
      ]);
    }
  }

  function updateQualityAttribute(attribute, qualityAttributeValueId) {
    const targetField = attribute.isPriceDriver ? "instrumentQualityAttributeValues" : "qualityAttributeValues";
    const existingValues = confirmation?.[targetField] || [];
    const existingValue = existingValues.find(
      (value) => value.qualityAttributeId === attribute.qualityAttributeId
    );
    const selectedValue = (product?.qualityAttributeValues || []).find(
      (value) => value.qualityAttributeValueId === qualityAttributeValueId
    );
    const remainingValues = existingValues.filter(
      (value) => value.qualityAttributeId !== attribute.qualityAttributeId
    );

    if (!qualityAttributeValueId) {
      setConfirmation({ ...confirmation, [targetField]: remainingValues });
      return;
    }

    const nextValue = {
      qualityAttributeId: attribute.qualityAttributeId,
      qualityAttributeValueId,
      value: selectedValue?.value,
      active: true,
    };

    if (!attribute.isPriceDriver) {
      nextValue.tradeConfirmationQualityAttributeValueId =
        existingValue?.tradeConfirmationQualityAttributeValueId || emptyGuid;
      nextValue.tradeConfirmationId = confirmation.tradeConfirmationId;
    }

    setConfirmation({
      ...confirmation,
      [targetField]: [...remainingValues, nextValue],
    });
  }

  function updateMetadataValue(field, value, attachment) {
    const existingValues = confirmation?.metadataValues || [];
    const existingValue = existingValues.find(
      (candidate) => candidate.fieldDefinitionId === field.fieldDefinitionId
    );
    const remainingValues = existingValues.filter(
      (candidate) => candidate.fieldDefinitionId !== field.fieldDefinitionId
    );

    if (!value && !attachment?.fileName) {
      setConfirmation({ ...confirmation, metadataValues: remainingValues });
      return;
    }

    setConfirmation({
      ...confirmation,
      metadataValues: [
        ...remainingValues,
        {
          tradeConfirmationMetadataValueId:
            existingValue?.tradeConfirmationMetadataValueId || emptyGuid,
          tradeConfirmationId: confirmation.tradeConfirmationId,
          fieldDefinitionId: field.fieldDefinitionId,
          value,
          fileName: attachment?.fileName || existingValue?.fileName,
          contentType: attachment?.contentType || existingValue?.contentType,
          active: true,
        },
      ],
    });
  }

  async function handleAttachmentSelected(field, file) {
    if (!file) return;

    setIsSaving(true);
    try {
      const stagedAttachment = await uploadTradeConfirmationAttachment(
        confirmation.tradeConfirmationId,
        field.fieldDefinitionId,
        file
      );
      updateMetadataValue(field, stagedAttachment.storageAddress, {
        fileName: stagedAttachment.fileName,
        contentType: stagedAttachment.contentType,
      });
      toast.info(t("AttachmentPendingStorage"));
    } catch (ex) {
      console.error("Failed to stage trade confirmation attachment", ex);
      toast.error(t("TradeConfirmationAttachmentFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  function validateConfirmation() {
    const validationData = {
      productId: confirmation?.productId,
      marketId: confirmation?.marketId,
      termId: confirmation?.termId,
      priceBasisId: confirmation?.priceBasisId,
      price: confirmation?.price,
      volume: confirmation?.volume,
      tradeDate: confirmation?.tradeDate,
    };
    const schema = {
      productId: Joi.string().required().label(t("Product")),
      marketId: Joi.string().required().label(t("Market")),
      termId: Joi.string().required().label(t("Term")),
      priceBasisId: Joi.string().required().label(t("PriceBasis")),
      price: Joi.number().required().label(t("Price")),
      volume: Joi.number().required().label(t("Volume")),
      tradeDate: Joi.date().required().label(t("TradeDate")),
    };
    qualityAttributes
      .filter((attribute) => attribute.isPriceDriver)
      .forEach((attribute) => {
        validationData[attribute.qualityAttributeId] = getQualityAttributeValue(
          confirmation,
          attribute
        )?.qualityAttributeValueId;
        schema[attribute.qualityAttributeId] = Joi.string()
          .required()
          .label(attribute.qualityAttributeName);
      });

    metadataDefinitions.forEach((field) => {
      const value = getMetadataValue(confirmation, field.fieldDefinitionId)?.value;
      validationData[field.fieldDefinitionId] = value;

      if (value === undefined || value === null || value === "" || field.fieldDataType === "Attachment") {
        schema[field.fieldDefinitionId] = Joi.any().optional();
        return;
      }

      if (field.fieldDataType === "Integer") {
        schema[field.fieldDefinitionId] = Joi.number().integer().label(field.fieldName);
      } else if (field.fieldDataType === "Decimal") {
        schema[field.fieldDefinitionId] = Joi.number().label(field.fieldName);
      } else if (field.fieldDataType === "Date" || field.fieldDataType === "DateTime") {
        schema[field.fieldDefinitionId] = Joi.date().label(field.fieldName);
      } else {
        schema[field.fieldDefinitionId] = Joi.any().optional();
      }
    });

    const { error } = Joi.validate(validationData, schema, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (!error) return null;

    return error.details.reduce((acc, item) => {
      acc[item.path[0]] = item.message;
      return acc;
    }, {});
  }

  function buildPayload(instrumentId) {
    return {
      tradeConfirmationId: confirmation.tradeConfirmationId,
      engineTradeId: confirmation.engineTradeId,
      actingOrganizationId,
      tradeDate: confirmation.tradeDate,
      buyerOrderId: confirmation.buyerOrderId,
      sellerOrderId: confirmation.sellerOrderId,
      instrumentId: instrumentId || confirmation.instrumentId,
      marketId: confirmation.marketId,
      termId: confirmation.termId,
      productId: confirmation.productId,
      priceBasisId: confirmation.priceBasisId,
      currencyId: confirmation.currencyId,
      uomId: confirmation.uomId,
      buyerOrganizationId: confirmation.buyerOrganizationId,
      sellerOrganizationId: confirmation.sellerOrganizationId,
      buyerUserAccountId: confirmation.buyerUserAccountId,
      sellerUserAccountId: confirmation.sellerUserAccountId,
      price: confirmation.price === "" ? null : Number(confirmation.price),
      volume: confirmation.volume === "" ? null : Number(confirmation.volume),
      active: true,
      eventId: uuid(),
      instrumentQualityAttributeValues: (confirmation.instrumentQualityAttributeValues || [])
        .filter((value) => value.qualityAttributeId && value.qualityAttributeValueId)
        .map((value) => ({
          qualityAttributeId: value.qualityAttributeId,
          qualityAttributeValueId: value.qualityAttributeValueId,
          value: value.value,
          active: value.active !== false,
        })),
      qualityAttributeValues: (confirmation.qualityAttributeValues || [])
        .filter((value) => value.qualityAttributeId && value.qualityAttributeValueId)
        .map((value) => ({
          tradeConfirmationQualityAttributeValueId:
            value.tradeConfirmationQualityAttributeValueId || emptyGuid,
          qualityAttributeId: value.qualityAttributeId,
          qualityAttributeValueId: value.qualityAttributeValueId,
          value: value.value,
          active: value.active !== false,
        })),
      metadataValues: (confirmation.metadataValues || [])
        .filter((value) => value.fieldDefinitionId)
        .map((value) => ({
          tradeConfirmationMetadataValueId: value.tradeConfirmationMetadataValueId || emptyGuid,
          fieldDefinitionId: value.fieldDefinitionId,
          value: value.value,
          fileName: value.fileName,
          contentType: value.contentType,
          active: value.active !== false,
        })),
    };
  }

  async function refreshAfterAction(savedConfirmation) {
    setConfirmation(savedConfirmation);
    if (savedConfirmation?.exists) {
      const loadedVersions = await getTradeConfirmationVersions(
        savedConfirmation.tradeConfirmationId,
        actingOrganizationId
      );
      const visibleVersions = getDisplayVersions(loadedVersions || []);
      setVersions(loadedVersions || []);
      setSelectedVersionIndex(visibleVersions.length ? visibleVersions.length - 1 : null);
      setReviewingVersions(false);
    }
    if (onSaved) onSaved(savedConfirmation);
  }


  async function resolveInstrumentId() {
    const selectedPriceBasis = getSelectedPriceBasis(confirmation);
    const instrument = await getOrAddInstrument({
      marketId: confirmation.marketId,
      termId: confirmation.termId,
      priceBasisId: confirmation.priceBasisId,
      currencyId: confirmation.currencyId || selectedPriceBasis?.currencyId,
      uomId: confirmation.uomId || selectedPriceBasis?.uomId,
      qualityAttributeValues: (confirmation.instrumentQualityAttributeValues || [])
        .filter((value) => value.qualityAttributeValueId)
        .map((value) => ({
          qualityAttributeId: value.qualityAttributeId,
          qualityAttributeValueId: value.qualityAttributeValueId,
          value: value.value,
          active: value.active !== false,
        })),
      active: true,
      eventId: uuid(),
    });

    return instrument?.instrumentId || instrument?.InstrumentId || confirmation.instrumentId;
  }

  async function handleSave() {
    const validationErrors = validateConfirmation();
    setErrors(validationErrors || {});
    if (validationErrors) {
      toast.error(t("TradeConfirmationValidationFailed"));
      return;
    }

    setIsSaving(true);
    try {
      const instrumentId = await resolveInstrumentId();
      const savedConfirmation = await saveTradeConfirmation(buildPayload(instrumentId));
      await refreshAfterAction(savedConfirmation);
      toast.success(t("TradeConfirmationSaved"));
    } catch (ex) {
      console.error("Failed to save trade confirmation", ex);
      toast.error(t("TradeConfirmationSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleLifecycleAction(action, successMessage) {
    setIsSaving(true);
    try {
      const savedConfirmation = await action({
        tradeConfirmationId: confirmation.tradeConfirmationId,
        engineTradeId: confirmation.engineTradeId,
        actingOrganizationId,
        eventId: uuid(),
      });
      await refreshAfterAction(savedConfirmation);
      toast.success(successMessage);
    } catch (ex) {
      console.error("Failed to update trade confirmation lifecycle", ex);
      toast.error(t("TradeConfirmationSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  }

  function renderReadOnly(label, value, changed) {
    return (
      <Form.Group className="mb-2">
        <Form.Label>{label}</Form.Label>
        <Form.Control
          size="sm"
          value={value ?? ""}
          readOnly
          className={getChangedClass(changed)}
        />
      </Form.Group>
    );
  }

  function renderSelectField(name, label, options, valueField, textField, changed) {
    return (
      <Form.Group className="mb-2">
        <Form.Label>{label}</Form.Label>
        <Form.Select
          size="sm"
          value={activeConfirmation?.[name] || ""}
          onChange={(event) => updateConfirmationField(name, event.target.value)}
          disabled={!canEdit}
          isInvalid={!!errors[name]}
          className={getChangedClass(changed)}
        >
          <option value="">{t("SelectValue")}</option>
          {options.map((option) => (
            <option key={option[valueField]} value={option[valueField]}>
              {option[textField]}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
      </Form.Group>
    );
  }

  function renderEditableField(name, label, type, changed) {
    const sourceValue = activeConfirmation?.[name];
    const value = type === "datetime-local" ? toDateTimeInputValue(sourceValue) : sourceValue ?? "";

    return (
      <Form.Group className="mb-2">
        <Form.Label>{label}</Form.Label>
        <Form.Control
          size="sm"
          type={type}
          value={value}
          onChange={(event) => updateConfirmationField(name, event.target.value)}
          readOnly={!canEdit}
          isInvalid={!!errors[name]}
          className={getChangedClass(changed)}
        />
        <Form.Control.Feedback type="invalid">{errors[name]}</Form.Control.Feedback>
      </Form.Group>
    );
  }

  function renderQualityAttribute(attribute) {
    const currentValue = getQualityAttributeValue(activeConfirmation, attribute);
    const options = (product?.qualityAttributeValues || []).filter(
      (value) => value.qualityAttributeId === attribute.qualityAttributeId
    );

    return (
      <Form.Group className="mb-2" key={attribute.qualityAttributeId}>
        <Form.Label>
          {attribute.qualityAttributeName}
          {attribute.isPriceDriver && <span className="trade-confirmation-required"> *</span>}
        </Form.Label>
        <Form.Select
          size="sm"
          value={currentValue?.qualityAttributeValueId || ""}
          onChange={(event) => updateQualityAttribute(attribute, event.target.value)}
          disabled={!canEdit}
          isInvalid={!!errors[attribute.qualityAttributeId]}
          className={getChangedClass(isQualityChanged(attribute))}
        >
          <option value="">{t("SelectValue")}</option>
          {options.map((option) => (
            <option key={option.qualityAttributeValueId} value={option.qualityAttributeValueId}>
              {option.value}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{errors[attribute.qualityAttributeId]}</Form.Control.Feedback>
      </Form.Group>
    );
  }

  function renderMetadataField(field) {
    const value = getMetadataValue(activeConfirmation, field.fieldDefinitionId);
    const currentValue = value?.value ?? field.defaultValue ?? "";
    const changedClass = getChangedClass(isMetadataChanged(field.fieldDefinitionId));
    const disabled = !canEdit;
    const options = getFieldOptions(field);

    if (field.fieldDataType === "MultiLineText") {
      return (
        <Form.Control
          size="sm"
          as="textarea"
          rows={3}
          value={currentValue}
          onChange={(event) => updateMetadataValue(field, event.target.value)}
          disabled={disabled}
          isInvalid={!!errors[field.fieldDefinitionId]}
          className={changedClass}
        />
      );
    }

    if (field.fieldDataType === "Select") {
      return (
        <Form.Select
          size="sm"
          value={currentValue}
          onChange={(event) => updateMetadataValue(field, event.target.value)}
          disabled={disabled}
          isInvalid={!!errors[field.fieldDefinitionId]}
          className={changedClass}
        >
          <option value="">{t("SelectValue")}</option>
          {options.map((option) => (
            <option key={option.fieldValueOptionId || option.value} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (field.fieldDataType === "MultiSelect") {
      const selectedValues = parseMultiSelectValue(currentValue);
      return (
        <Form.Select
          size="sm"
          multiple
          value={selectedValues}
          onChange={(event) =>
            updateMetadataValue(
              field,
              JSON.stringify(Array.from(event.target.selectedOptions).map((option) => option.value))
            )
          }
          disabled={disabled}
          isInvalid={!!errors[field.fieldDefinitionId]}
          className={changedClass}
        >
          {options.map((option) => (
            <option key={option.fieldValueOptionId || option.value} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (field.fieldDataType === "Boolean") {
      return (
        <Form.Check
          type="checkbox"
          checked={currentValue === true || currentValue === "true"}
          onChange={(event) => updateMetadataValue(field, event.target.checked ? "true" : "false")}
          disabled={disabled}
          className={changedClass}
        />
      );
    }

    if (field.fieldDataType === "Attachment") {
      return (
        <div>
          <Form.Control
            size="sm"
            type="file"
            onChange={(event) => handleAttachmentSelected(field, event.target.files[0])}
            disabled={disabled}
            className={changedClass}
          />
          {value?.fileName && (
            <div className="small mt-1">
              {value.fileName} <span className="text-muted">({t("AttachmentPendingStorage")})</span>
            </div>
          )}
          {value?.value && <div className="small text-muted text-break">{value.value}</div>}
        </div>
      );
    }

    const inputType = field.fieldDataType === "Date" ? "date" : field.fieldDataType === "DateTime" ? "datetime-local" : "text";
    const typedValue =
      field.fieldDataType === "Date"
        ? toDateInputValue(currentValue)
        : field.fieldDataType === "DateTime"
          ? toDateTimeInputValue(currentValue)
          : currentValue;

    return (
      <Form.Control
        size="sm"
        type={field.fieldDataType === "Integer" || field.fieldDataType === "Decimal" ? "number" : inputType}
        value={typedValue}
        onChange={(event) => updateMetadataValue(field, event.target.value)}
        disabled={disabled}
        isInvalid={!!errors[field.fieldDefinitionId]}
        className={changedClass}
      />
    );
  }

  function renderVersionControls() {
    if (!displayVersions.length || currentVersionIndex === null) return null;

    function goToVersion(index) {
      const nextIndex = Math.max(0, Math.min(displayVersions.length - 1, index));
      setSelectedVersionIndex(nextIndex);
      setReviewingVersions(nextIndex !== displayVersions.length - 1);
    }

    return (
      <div className="trade-confirmation-pagination">
        <Button
          size="sm"
          variant="outline-primary"
          disabled={currentVersionIndex <= 0}
          onClick={() => goToVersion(0)}
        >
          {"<<"}
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={currentVersionIndex <= 0}
          onClick={() => goToVersion(currentVersionIndex - 1)}
        >
          {"<"}
        </Button>
        <span className="trade-confirmation-pagination-label">
          {t("TradeConfirmationVersion", {
            current: currentVersionIndex + 1,
            total: displayVersions.length,
          })}
        </span>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={currentVersionIndex >= displayVersions.length - 1}
          onClick={() => goToVersion(currentVersionIndex + 1)}
        >
          {">"}
        </Button>
        <Button
          size="sm"
          variant="outline-primary"
          disabled={currentVersionIndex >= displayVersions.length - 1}
          onClick={() => goToVersion(displayVersions.length - 1)}
        >
          {">>"}
        </Button>
        <span className={`trade-confirmation-current-indicator${reviewingVersions ? " trade-confirmation-current-indicator-hidden" : ""}`}>
          {t("CurrentVersion")}
        </span>
      </div>
    );
  }

  function renderFooter() {
    if (reviewingVersions) {
      return (
        <Button size="sm" variant="secondary" onClick={onHide}>
          {t("Close")}
        </Button>
      );
    }

    return (
      <>
        <Button size="sm" variant="secondary" onClick={onHide} disabled={isSaving}>
          {t("Close")}
        </Button>
        {canReject && (
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => {
              if (window.confirm(t("ConfirmAbandonTradeConfirmation"))) {
                handleLifecycleAction(rejectTradeConfirmation, t("TradeConfirmationRejected"));
              }
            }}
            disabled={isSaving}
          >
            {t("Abandon")}
          </Button>
        )}
        {canCancel && (
          <Button
            size="sm"
            variant="danger"
            onClick={() => {
              if (window.confirm(t("ConfirmCancelTradeConfirmation"))) {
                handleLifecycleAction(cancelTradeConfirmation, t("TradeConfirmationCancelled"));
              }
            }}
            disabled={isSaving}
          >
            {t("CancelTradeConfirmation")}
          </Button>
        )}
        {canAccept && (
          <Button
            size="sm"
            variant="success"
            onClick={() => handleLifecycleAction(acceptTradeConfirmation, t("TradeConfirmationAccepted"))}
            disabled={isSaving}
          >
            {t("Accept")}
          </Button>
        )}
        {canEdit && (
          <Button size="sm" variant="primary" onClick={handleSave} disabled={isSaving}>
            {confirmation?.exists ? t("SendResponse") : t("InitiateConfirmation")}
          </Button>
        )}
      </>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" centered className="trade-confirmation-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {t("TradeConfirmation")} {activeConfirmation?.engineTradeId && (
            <span className="small text-muted">{t("EngineTradeId")}: {activeConfirmation.engineTradeId}</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="trade-confirmation-body">
        {isLoading || !activeConfirmation ? (
          <div className="d-flex justify-content-center p-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <Badge bg={getStatusVariant(activeConfirmation.lifecycleStatus)}>
                  {t(activeConfirmation.lifecycleStatus)}
                </Badge>
                {!canEdit && activeConfirmation.lifecycleStatus === "Pending" && !reviewingVersions && (
                  <span className="small text-muted ms-2">{t("AwaitingCounterpartyResponse")}</span>
                )}
                {reviewingVersions && <span className="small text-muted ms-2">{t("ReviewingPriorVersion")}</span>}
              </div>
            </div>

            <Row>
              <Col lg={8}>
                <Card className="mb-3 trade-confirmation-panel">
                  <Card.Header>{t("CoreTradeDetails")}</Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>{renderReadOnly(t("BuyerOrganization"), activeConfirmation.buyerOrganizationName)}</Col>
                      <Col md={6}>{renderReadOnly(t("SellerOrganization"), activeConfirmation.sellerOrganizationName)}</Col>
                    </Row>
                    <Row>
                      <Col md={6}>{renderSelectField("productId", t("Product"), productOptions, "productId", "name", isCoreChanged("productId"))}</Col>
                      <Col md={6}>{renderSelectField("marketId", t("Market"), marketOptions, "marketId", "name", isCoreChanged("marketId"))}</Col>
                    </Row>
                    <Row>
                      <Col md={6}>{renderSelectField("priceBasisId", t("PriceBasis"), priceBasisOptions, "priceBasisId", "priceBasisName", isCoreChanged("priceBasisId"))}</Col>
                      <Col md={6}>{renderSelectField("termId", t("Term"), terms, "termId", "name", isCoreChanged("termId"))}</Col>
                    </Row>
                    <Row>
                      <Col md={6}>{renderEditableField("price", t("Price") + " (" + (activeConfirmation.currencySymbol || activeConfirmation.currencyName || "") + ")", "number", isCoreChanged("price"))}</Col>
                      <Col md={6}>{renderEditableField("volume", t("Volume") + " (" + (activeConfirmation.uomName || "") + ")", "number", isCoreChanged("volume"))}</Col>
                    </Row>
                    <Row>
                      <Col md={6}>{renderReadOnly(t("TradeDate"), formatVersionDate(activeConfirmation.tradeDate), isCoreChanged("tradeDate"))}</Col>
                    </Row>
                    {qualityAttributes.length > 0 && (
                      <Row className="mt-2">
                        {qualityAttributes.map((attribute) => (
                          <Col md={6} key={attribute.qualityAttributeId}>
                            {renderQualityAttribute(attribute)}
                          </Col>
                        ))}
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                {Object.keys(metadataGroups).length === 0 ? (
                  <div className="text-muted small">{t("NoTradeNegotiationMetadataFields")}</div>
                ) : (
                  Object.keys(metadataGroups).map((groupName) => (
                    <Card className="mb-2 trade-confirmation-group" key={groupName}>
                      <Card.Header className="d-flex justify-content-between align-items-center py-2">
                        <span className="trade-confirmation-group-title">
                          {groupName}
                          {isMetadataGroupChanged(groupName) && <span className="trade-confirmation-group-changed" aria-label={t("Changed")} />}
                        </span>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => setOpenGroups({ ...openGroups, [groupName]: !openGroups[groupName] })}
                        >
                          {openGroups[groupName] ? "-" : "+"}
                        </Button>
                      </Card.Header>
                      <Collapse in={openGroups[groupName]}>
                        <Card.Body>
                          {_.orderBy(metadataGroups[groupName], ["sortOrder", "fieldName"]).map((field) => (
                            <Form.Group className="mb-3" key={field.fieldDefinitionId}>
                              <Form.Label>{field.fieldName}</Form.Label>
                              {renderMetadataField(field)}
                              {errors[field.fieldDefinitionId] && (
                                <div className="invalid-feedback d-block">
                                  {errors[field.fieldDefinitionId]}
                                </div>
                              )}
                            </Form.Group>
                          ))}
                        </Card.Body>
                      </Collapse>
                    </Card>
                  ))
                )}
              </Col>
            </Row>

            {activeConfirmation.updatedDate && (
              <div className="small text-muted mt-2">
                {t("Updated")} {formatVersionDate(activeConfirmation.updatedDate)}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="trade-confirmation-footer">
        {renderVersionControls()}
        <div className="trade-confirmation-actions">
          {isSaving && <Spinner size="sm" animation="border" />}
          {renderFooter()}
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default withTranslation(["trading"])(TradeConfirmationModal);


